import { AfterViewInit, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { DashboardControl, ResourceManager, WorkingMode } from 'devexpress-dashboard';

@Component({
  selector: 'abc-dashboard',
  template: `<div [class]="'dashboard-container'"></div>`,
  styleUrls: ['./abc-dashboard.component.scss'],
  standalone: true,
})
export class _AbcDashboardComponent implements AfterViewInit, OnDestroy {
  @Input() endPoint: string;
  @Input() workingMode: WorkingMode;
  @Input() ajaxHeaders: { [key: string]: any };
  @Input() autoInitialize: boolean = true;
  @Input() mobileLayoutEnabledInput: string = 'Auto';
  @Input() set initialDashboardId(value: string) {
    this._initialDashboardId = value;
    if (this.dashboardControl) {
      this.refreshControl();
    }
  }
  get initialDashboardId(): string {
    return this._initialDashboardId;
  }

  private _initialDashboardId: string;
  private dashboardControl!: DashboardControl;
  private styleableMutationObserver: MutationObserver;
  private styleableHtmlElements: HTMLElement[] = [];

  constructor(private element: ElementRef) {
    ResourceManager.embedBundledResources();
    this.applyHtmlStyle(element.nativeElement);
  }

  ngAfterViewInit(): void {
    if (this.autoInitialize) {
      this.refreshControl();
    }
  }

  private refreshControl(): void {
    this.dashboardControl?.dispose();
    const dashboardContainer: HTMLElement = this.element.nativeElement.querySelector('.dashboard-container');
    dashboardContainer.innerHTML = '';
    this.dashboardControl = new DashboardControl(dashboardContainer, {
      endpoint: this.endPoint ?? 'https://demos.devexpress.com/services/dashboard/api',
      workingMode: this.workingMode ?? 'ViewerOnly',
      initialDashboardId: this.initialDashboardId,
      ajaxRemoteService: {
        headers: this.ajaxHeaders,
      },
      extensions: {
        'mobile-layout': {
          mobileLayoutEnabled: this.mobileLayoutEnabledInput,
        },
        'dashboard-export': {
          allowExportDashboard: true,
          allowExportDashboardItems: true,
        },
      },
    });
    this.dashboardControl.render();
  }

  ngOnDestroy(): void {
    this.dashboardControl?.dispose();
  }

  public setEndPoint(endPoint: string, ajaxHeaders: { [key: string]: any }): void {
    this.endPoint = endPoint;
    this.ajaxHeaders = ajaxHeaders;

    this.refreshControl();
  }

  private applyHtmlStyle(domElement: HTMLElement) {
    this.styleableHtmlElements = [];
    if (!this.styleableMutationObserver) {
      let isMutating: boolean = false;
      this.styleableMutationObserver = new MutationObserver((arr) => {
        if (
          !isMutating &&
          arr?.find((_) => _.target instanceof HTMLElement && !this.styleableHtmlElements.includes(_.target))
        ) {
          isMutating = true;
          try {
            this.applyAriaLabelText(Array.from(domElement.getElementsByClassName('dx-tab')));
          } finally {
            isMutating = false;
          }
        }
      });
    }

    this.styleableMutationObserver.observe(domElement, {
      subtree: true,
      childList: true,
    });
  }

  private applyAriaLabelText(elements: Node[]) {
    if (elements) {
      for (const element of elements) {
        if (element instanceof HTMLElement) {
          const label = element.getAttribute('aria-label');
          if (label) {
            if (!this.styleableHtmlElements.includes(element)) {
              this.styleableHtmlElements.push(element);
            }
            element.innerText = label;
          } else {
            this.applyAriaLabelText(Array.from(element.childNodes));
          }
        }
      }
    }
  }
}
